import { useCallback } from 'react';
import { useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import type { WorkspaceObjects as WS } from '@trinity/chat/api/interfaces/workspace.object';
import { WorkspaceObjectType } from '@trinity/chat';
import { useAppSelector } from '@/store';
import { FetchWorkspaceFiles } from '@trinity/chat/api/interfaces/workspaces';

type Props = FetchWorkspaceFiles.Request;
type Data = FetchWorkspaceFiles.Response;
type QueryKey = readonly [string, Props];
type Options = UseQueryOptions<Data, AxiosError, Data, QueryKey>;

export const useFetchObjectChildrenQuery = (props: Props, options?: Options) => {
  const workspaceBrandsEnabled = useAppSelector(state => state.features.workspaceBrands);

  const apiClient = useChatApiClient();

  const query = useQuery({
    queryKey: [
      `get:workspaces/objects`,
      props,
    ],
    queryFn: ({ queryKey }) => {
      return apiClient.workspaces.fetchWorkspaceFiles({
        workspaceId: queryKey[1].workspaceId,
        objectId: queryKey[1].objectId,
      }).then(res => ({
        ...res,
        items: res.items.filter(f => workspaceBrandsEnabled ? true : f.object.typeId !== WorkspaceObjectType.Brand),
      }));
    },
    ...options,
  });

  return query;
};

export const useInvalidateObjectChildrenQuery = () => {
  const queryClient = useQueryClient();

  return useCallback(() => {
    return queryClient.invalidateQueries({ queryKey: [`get:workspace/object/children`] });
  }, [queryClient]);
};